/**
 * Official profile · Money (screenshots/30). Stats; Top organizational donors since 2019 (Donors / Employers;
 * private individuals are left out of the donor list). Donor totals break down by source (company / employees /
 * lobbyists) with an "All sources / Company only" switch and the overlap note whenever lobbyist money is in a
 * total, using the Organizations tab's rules (organizations/logic.ts, brief §2.10); Votes on items their donors lobbied (the forecast's
 * money-linked input; shown once the positions-per-matter producer exists, backend 13); Fundraising before council
 * meetings (30-day totals vs. usual pace, 1× tick); side: From registered lobbyists, Lobbied legislation they
 * sponsored, Most donors in common with, sources. Always: "Money near a vote is not evidence that it influenced
 * the vote."
 */
import { useEffect, useRef, useState } from "react";
import { Face } from "../components/shared/Face";
import { List, ListRow, RowCell, RowMain, RowSub, RowTitle } from "../components/shared/List";
import { prefersReducedMotion, recipes } from "../motion";
import { Tile } from "./VotesTab";
import { amountFor, clientSlug, needsOverlapNote, type MoneyView } from "../organizations/logic";
import { mdy, money, moneyShort } from "./atoms";
import type { OfficialRecord } from "./model";

export function MoneyTab({ rec }: { rec: OfficialRecord }) {
  const m = rec.money;
  const [by, setBy] = useState<"donors" | "employers">("donors");
  const [view, setView] = useState<MoneyView>("all");
  // ?org=<slug> (Organizations "Jump to the member", screenshots/66 #6): highlight that donor and flash it once (600 ms).
  const [org] = useState(() => new URLSearchParams(location.search).get("org"));
  const orgRow = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = orgRow.current;
    if (!el) return;
    el.scrollIntoView({ block: "center", behavior: prefersReducedMotion() ? "auto" : "smooth" });
    el.dataset.flash = "";
    const t = setTimeout(() => delete el.dataset.flash, recipes.snapshotToLive.flashMs);
    return () => clearTimeout(t);
  }, []);
  if (!m) return null;
  const rows = by === "donors"
    ? [...m.donors].map((d) => ({ ...d, total: amountFor(d.money, view) })).filter((d) => d.total > 0).sort((x, y) => y.total - x.total)
    : m.employers.map((e) => ({ ...e, lobbyClient: false, money: null }));
  const max = Math.max(1, ...rows.map((r) => r.total));
  const overlap = by === "donors" && m.donors.some((d) => needsOverlapNote(d.money, view));
  const maxTimes = Math.max(1, ...m.spikes.map((s) => s.times));
  return (
    <div className="p-cols">
      <div className="p-main p-main--32">
        <div className="p-tiles">
          <Tile v={money(m.cashOnHand)} l="Cash on hand" s={`as of ${mdy(m.cashAsOf, { month: "short", day: "numeric", year: "numeric" })}`} />
          <Tile v={money(m.raisedThisTerm)} l="Raised this term" s="itemized gifts of $150+" />
          <Tile v={moneyShort(m.raisedSince2019)} l="Raised since 2019" s={money(m.raisedSince2019)} />
          <Tile v={`${Math.round(m.shareChicago * 100)}%`} l="From Chicago addresses" s={`${Math.round(m.shareIllinois * 100)}% from Illinois`} />
        </div>

        <section>
          <div className="p-money-head">
            <h2>Top organizational donors since 2019</h2>
            {m.committee && <span className="count-badge">{m.committee}</span>}
            <div className="p-seg p-seg--right" role="radiogroup" aria-label="Group by">
              <button role="radio" aria-checked={by === "donors"} onClick={() => setBy("donors")}>Donors</button>
              <button role="radio" aria-checked={by === "employers"} onClick={() => setBy("employers")}>Employers</button>
            </div>
          </div>
          {by === "donors" && (
            <div className="p-seg p-money-view" role="radiogroup" aria-label="Money view">
              <button role="radio" aria-checked={view === "all"} onClick={() => setView("all")}>All sources</button>
              <button role="radio" aria-checked={view === "company"} onClick={() => setView("company")}>Company only</button>
            </div>
          )}
          <List as="div" variant="plain" className="p-mtable" label="Top donors" role="table">
            <ListRow as="div" className="p-mrow p-mrow--head" role="row">
              <RowMain min={160}><span role="columnheader">{by === "donors" ? "DONOR" : "EMPLOYER"}</span></RowMain>
              <RowCell width={220}><span role="columnheader">TOTAL</span></RowCell>
              <RowCell width={44} align="end"><span role="columnheader">GIFTS</span></RowCell>
            </ListRow>
            {rows.map((r) => (
              <ListRow as="div" key={r.name} className="p-mrow" role="row" data-org={(org && by === "donors" && clientSlug(r.name) === org) || undefined}
                ref={org && by === "donors" && clientSlug(r.name) === org ? orgRow : undefined}>
                <RowMain min={160}>
                  <span role="cell" className="p-mrow__who">
                    <span className="p-mrow__name"><RowTitle as="b">{r.name}</RowTitle>{r.lobbyClient && <span className="p-client">Lobbying client</span>}</span>
                    {r.money && <RowSub wrap><small>{[["company", r.money.company], ["employees", r.money.employees], ...(view === "all" ? [["lobbyists", r.money.lobbyists]] : [])]
                      .filter(([, v]) => Number(v) > 0).map(([k, v]) => `${money(Number(v))} ${k}`).join(" · ")}</small></RowSub>}
                  </span>
                </RowMain>
                <RowCell width={220}>
                  <span role="cell" className="p-mrow__total">
                    <span className="p-mrow__bar">
                      {r.money ? <>
                        <span style={{ width: `${(r.money.company / max) * 130}px` }} />
                        <span className="is-emp" style={{ left: `${(r.money.company / max) * 130}px`, width: `${(r.money.employees / max) * 130}px` }} />
                        {view === "all" && <span className="is-lob" style={{ left: `${((r.money.company + r.money.employees) / max) * 130}px`, width: `${(r.money.lobbyists / max) * 130}px` }} />}
                      </> : <span style={{ width: `${(r.total / max) * 130}px` }} />}
                    </span>
                    <b>{money(r.total)}</b>
                  </span>
                </RowCell>
                <RowCell width={44} align="end"><span role="cell" className="p-mrow__gifts">{r.count}</span></RowCell>
              </ListRow>
            ))}
          </List>
          {overlap && <p className="caveat p-overlap">Lobbyists' own contributions count under every client they represent, so these totals overlap. Switch to "Company only" to see what each organization and its employees gave.</p>}
        </section>

        {m.moneyLinked && m.moneyLinked.length > 0 && (
          <section>
            <div className="p-money-head p-money-head--stack">
              <h2>Votes on items their donors lobbied</h2>
            </div>
            <List as="div" variant="plain" className="p-mtable" label="Money-linked votes" role="table">
              <ListRow as="div" className="p-lrow p-lrow--head" role="row">
                <RowMain min={140}><span role="columnheader">CLIENT</span></RowMain>
                <RowCell width={110}><span role="columnheader">GAVE SINCE 2019</span></RowCell>
                <RowCell width={60}><span role="columnheader">VOTES</span></RowCell>
                <RowCell width={170}><span role="columnheader">THEIR WAY</span></RowCell>
              </ListRow>
              {m.moneyLinked.map((x) => (
                <ListRow as="div" key={x.client} className="p-lrow" role="row">
                  <RowMain min={140}><span role="cell"><RowTitle as="b">{x.client}</RowTitle></span></RowMain>
                  <RowCell width={110}><span role="cell">{money(x.gave)}</span></RowCell>
                  <RowCell width={60}><span role="cell" className="p-muted2">{x.n}</span></RowCell>
                  <RowCell width={170}><span role="cell" className="p-theirway">
                    <span className="p-theirway__dots">{Array.from({ length: x.n }, (_, i) => <i key={i} data-on={i < x.k || undefined} />)}</span>
                    <span>{x.k} of {x.n}{x.n < 2 ? " · too few" : ""}</span>
                  </span></RowCell>
                </ListRow>
              ))}
            </List>
            <p className="caveat">Money near a vote isn't evidence of influence.</p>
          </section>
        )}

        {m.spikes.length > 0 && (
          <section className="p-spikes">
            <h2>Fundraising before council meetings</h2>
            <p className="p-sec-sub p-sec-sub--md">30 days before each meeting vs. their usual pace</p>
            {m.spikes.map((s) => (
              <div key={s.date} className="p-spike">
                <span>{mdy(s.date, { month: "short", day: "numeric", year: "numeric" })}</span>
                <span className="p-spike__track"><span className="p-spike__fill" style={{ width: `${(s.times / maxTimes) * 100}%` }} /><span className="p-spike__tick" style={{ left: `${(1 / maxTimes) * 100}%` }} /></span>
                <b>{s.times}× · {money(s.raised)}</b>
              </div>
            ))}
            <span className="p-legend">Black tick = usual 30-day pace (1×)</span>
            {!m.moneyLinked?.length && <p className="caveat">Money near a vote isn't evidence of influence.</p>}
          </section>
        )}
      </div>
      <aside className="p-side">
        <section className="p-card">
          <h3>From registered lobbyists</h3>
          <div className="p-lobstats">
            <span><b>{money(m.lobbyists.total)}</b><small>{m.lobbyists.count} contributions</small></span>
            <span><b>{m.lobbyists.gifts}</b><small>gifts &amp; hosting reported</small></span>
          </div>
          {m.lobbyists.top.map((l) => <div key={l.name} className="p-kv"><span>{l.name}</span><small>{money(l.total)} · {l.count}</small></div>)}
        </section>
        {m.lobbied.length > 0 && (
          <section className="p-card">
            <h3>Lobbied legislation they sponsored</h3>
            {m.lobbied.map((x) => (
              <a key={x.matterId} className="p-lobbied" href={`/dashboard/legislation/${encodeURIComponent(x.recordId)}`}>
                <b>{x.title}</b><small>{x.filings} {x.filings === 1 ? "filing" : "filings"} · {x.clients.join(", ")}</small>
              </a>
            ))}
          </section>
        )}
        {m.shared.length > 0 && (
          <section className="p-card">
            <h3>Most donors in common with</h3>
            {m.shared.map((s) => (
              <a key={s.ward} className="p-shared" href={`/dashboard/people/a:${s.ward}`}>
                <Face name={s.name} photoUrl={s.photoUrl} size={26} />
                <span><b>{s.name}</b><small>Ward {s.ward}</small></span>
                <span className="count-badge">{s.shared} donors</span>
              </a>
            ))}
          </section>
        )}
        <p className="caveat p-sources">Sources: ISBE and Board of Ethics, daily. Donor flags are name matches.</p>
      </aside>
    </div>
  );
}
